import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import { toast } from "sonner";
import { Plus, Trash2, MessageSquare } from "lucide-react";
import { Avatar } from "../components/ui-bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const GRID_COLS = "grid-cols-[1fr_110px_110px_110px_110px_48px]";

function memberStats(tasks, uid) {
  const assigned = tasks.filter((t) => !t.parent_id && (t.assignees || []).includes(uid));
  return { assigned: assigned.length, open: assigned.filter((t) => t.status !== "done").length };
}

function RoleControl({ u, isAdmin, selfId, onChangeRole, testid }) {
  if (isAdmin && u.id !== selfId) {
    return (
      <Select value={u.role} onValueChange={(v) => onChangeRole(u, v)}>
        <SelectTrigger data-testid={`${testid}-${u.id}`} className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="admin">Admin</SelectItem>
          <SelectItem value="estimator">Estimator</SelectItem>
          <SelectItem value="designer">Designer</SelectItem>
          <SelectItem value="member">Member</SelectItem>
          <SelectItem value="pelaksana">Pelaksana</SelectItem>
        </SelectContent>
      </Select>
    );
  }
  return <span className="text-xs font-semibold uppercase tracking-wider text-navy dark:text-blue-300 bg-muted rounded px-1.5 py-0.5">{u.role}</span>;
}

function PresenceLabel({ u, testid }) {
  return (
    <span className="flex items-center gap-1.5 text-xs" data-testid={`${testid}-${u.id}`}>
      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: u.online ? "#22C55E" : "#EF4444" }} />
      {u.online ? "Online" : "Offline"}
    </span>
  );
}

function MemberActions({ u, isAdmin, selfId, onMessage, onRemove, suffix }) {
  return (
    <div className="flex items-center gap-1">
      {u.id !== selfId && (
        <button data-testid={`message-member${suffix}-${u.id}`} onClick={() => onMessage(u)} title="Send direct message"
          className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-[#1B2A6B] hover:bg-muted transition-colors">
          <MessageSquare size={14} />
        </button>
      )}
      {isAdmin && u.id !== selfId && (
        <button data-testid={`remove-member${suffix}-${u.id}`} onClick={() => onRemove(u)}
          className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-[#E31E24] hover:bg-[#E31E24]/10 transition-colors">
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

export default function Team() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { users, setUsers, tasks } = useData();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === "admin";

  const addMember = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/users", form);
      setUsers((p) => [...p, data]);
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "member" });
      toast.success("Team member added");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const changeRole = async (u, role) => {
    try {
      const { data } = await api.patch(`/users/${u.id}`, { role });
      setUsers((p) => p.map((x) => (x.id === u.id ? data : x)));
      toast.success("Role updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeMember = async (u) => {
    if (!window.confirm(`Remove ${u.name} from the workspace?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((p) => p.filter((x) => x.id !== u.id));
      toast.success("Member removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onMessage = (u) => navigate(`/chat?dm=${u.id}`);

  return (
    <div className="p-4 md:p-6" data-testid="team-page">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-heading text-xl font-bold tracking-tight">Team members</h2>
        <span className="text-sm text-muted-foreground" data-testid="team-count">{users.length} people</span>
        {isAdmin && (
          <button data-testid="add-member-button" onClick={() => setOpen(true)}
            className="ml-auto h-8 px-3 flex items-center gap-1.5 text-sm font-medium rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white transition-colors">
            <Plus size={15} />Add member
          </button>
        )}
      </div>

      {/* Mobile: card list */}
      <div className="md:hidden space-y-2" data-testid="team-cards-mobile">
        {users.map((u) => {
          const s = memberStats(tasks, u.id);
          return (
            <div key={u.id} data-testid={`team-member-m-${u.id}`} className="rounded-lg border border-border bg-card p-3 space-y-2.5">
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar name={u.name} userId={u.id} size="lg" online={u.online} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{u.name}{u.id === user?.id && <span className="text-xs text-muted-foreground"> (you)</span>}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </div>
                <MemberActions u={u} isAdmin={isAdmin} selfId={user?.id} onMessage={onMessage} onRemove={removeMember} suffix="-m" />
              </div>
              <div className="flex items-center justify-between gap-2">
                <PresenceLabel u={u} testid="presence-label-m" />
                <RoleControl u={u} isAdmin={isAdmin} selfId={user?.id} onChangeRole={changeRole} testid="role-select-m" />
                <span className="text-xs text-muted-foreground" data-testid={`member-stats-m-${u.id}`}>{s.assigned} assigned · {s.open} open</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop: grid table */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
        <div className="min-w-[680px] bg-card">
        <div className={`grid ${GRID_COLS} gap-2 px-4 py-2 border-b border-border bg-surface text-[10px] font-semibold uppercase tracking-wider text-muted-foreground`}>
          <span>Member</span><span>Status</span><span>Role</span><span>Assigned</span><span>Open tasks</span><span />
        </div>
        {users.map((u) => {
          const s = memberStats(tasks, u.id);
          return (
            <div key={u.id} data-testid={`team-member-${u.id}`} className={`grid ${GRID_COLS} gap-2 px-4 py-2.5 border-b border-border last:border-0 items-center`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar name={u.name} userId={u.id} size="lg" online={u.online} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{u.name}{u.id === user?.id && <span className="text-xs text-muted-foreground"> (you)</span>}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </div>
              </div>
              <PresenceLabel u={u} testid="presence-label" />
              <div>
                <RoleControl u={u} isAdmin={isAdmin} selfId={user?.id} onChangeRole={changeRole} testid="role-select" />
              </div>
              <span className="text-sm">{s.assigned}</span>
              <span className="text-sm">{s.open}</span>
              <MemberActions u={u} isAdmin={isAdmin} selfId={user?.id} onMessage={onMessage} onRemove={removeMember} suffix="" />
            </div>
          );
        })}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="add-member-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add team member</DialogTitle></DialogHeader>
          <form onSubmit={addMember} className="space-y-3">
            <input data-testid="member-name-input" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full h-9 px-3 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
            <input data-testid="member-email-input" required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full h-9 px-3 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
            <input data-testid="member-password-input" required minLength={6} type="password" placeholder="Temporary password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full h-9 px-3 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="member-role-select" className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="estimator">Estimator</SelectItem>
                <SelectItem value="designer">Designer</SelectItem>
                <SelectItem value="pelaksana">Pelaksana</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" data-testid="member-cancel" onClick={() => setOpen(false)} className="h-8 px-3 text-sm rounded-md border border-border hover:bg-muted transition-colors">Cancel</button>
              <button type="submit" data-testid="member-submit" disabled={saving} className="h-8 px-4 text-sm rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white font-medium transition-colors disabled:opacity-60">
                {saving ? "Adding…" : "Add member"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
